export interface WeatherData {
  temperature: number;
  condition: string;
  description: string;
  icon: string;
  humidity: number;
  windSpeed: number;
  location: string;
  isNight: boolean;
  moonPhase: string;
  moonIllumination: number;
}

export interface WeatherConfig {
  city: string;
  zipCode: string;
  units: 'metric' | 'imperial';
  updateInterval: number; // in minutes
}

class WeatherService {
  private config: WeatherConfig = {
    city: 'Miami',
    zipCode: '33101',
    units: 'imperial',
    updateInterval: 30
  };

  private cache: WeatherData | null = null;
  private lastUpdate: number = 0;

  constructor() {
    // Load config from localStorage if available
    const savedConfig = localStorage.getItem('weatherConfig');
    if (savedConfig) {
      this.config = { ...this.config, ...JSON.parse(savedConfig) };
    }
  }

  setConfig(config: Partial<WeatherConfig>): void {
    this.config = { ...this.config, ...config };
    localStorage.setItem('weatherConfig', JSON.stringify(this.config));
  }

  setUserLocation(address: { city?: string; state?: string; zipCode?: string; country?: string }): void {
    if (address.zipCode) {
      this.config.zipCode = address.zipCode;
    }
    if (address.city) {
      this.config.city = address.city;
    }
    
    // Save to localStorage
    localStorage.setItem('weatherConfig', JSON.stringify(this.config));
  }

  getConfig(): WeatherConfig {
    return { ...this.config };
  }

  private getMoonPhase(date: Date): { phase: string; illumination: number } {
    // Simple moon phase calculation
    // Reference: New Moon on Jan 11, 2024
    const referenceDate = new Date('2024-01-11T11:57:00Z');
    const lunarCycle = 29.53059;
    const diffDays = (date.getTime() - referenceDate.getTime()) / (1000 * 60 * 60 * 24);
    const age = diffDays % lunarCycle;
    const illumination = Math.abs(Math.sin((age / lunarCycle) * Math.PI) * 100);

    let phase: string;
    if (age < 1.84566) phase = 'New Moon';
    else if (age < 5.53699) phase = 'Waxing Crescent';
    else if (age < 9.22831) phase = 'First Quarter';
    else if (age < 12.91963) phase = 'Waxing Gibbous';
    else if (age < 16.61096) phase = 'Full Moon';
    else if (age < 20.30228) phase = 'Waning Gibbous';
    else if (age < 23.99361) phase = 'Last Quarter';
    else if (age < 27.68493) phase = 'Waning Crescent';
    else phase = 'New Moon';

    return { phase, illumination: Math.round(illumination) };
  }

  async getCurrentWeather(): Promise<WeatherData | null> {
    const now = Date.now();
    const cacheValid = this.cache && (now - this.lastUpdate) < (this.config.updateInterval * 60 * 1000);

    if (cacheValid) {
      return this.cache;
    }

    try {
      // Use hardcoded coordinates for common ZIP codes
      let lat: number, lon: number;
      
      if (this.config.zipCode === '33160' || this.config.zipCode === '33161' || this.config.zipCode === '33101') {
        lat = 25.9331; lon = -80.1391; // North Miami Beach / Sunny Isles
      } else if (this.config.zipCode === '10001') {
        lat = 40.7505; lon = -73.9934;
      } else if (this.config.zipCode === '90210') {
        lat = 34.0736; lon = -118.4004;
      } else {
        lat = 25.7617; lon = -80.1918; // Default Miami
      }
      
      const pointsResponse = await fetch(`https://api.weather.gov/points/${lat},${lon}`);
      if (!pointsResponse.ok) throw new Error(`Weather points API error: ${pointsResponse.status}`);
      
      const pointsData = await pointsResponse.json();
      const forecastUrl = pointsData.properties.forecastHourly || pointsData.properties.forecast;

      const response = await fetch(forecastUrl);
      if (!response.ok) throw new Error(`Weather API error: ${response.status}`);

      const data = await response.json();
      const periods = data.properties.periods;
      
      // Find the most relevant period (the one that starts before or at now and ends after now)
      const currentTime = new Date();
      let currentPeriod = periods.find((p: any) => {
        const start = new Date(p.startTime);
        const end = new Date(p.endTime);
        return currentTime >= start && currentTime < end;
      });

      // Fallback to first period if no exact match found
      if (!currentPeriod) {
        currentPeriod = periods[0];
      }
      
      let temperature = currentPeriod.temperature;
      if (this.config.units === 'metric') {
        temperature = Math.round((temperature - 32) * 5/9);
      }

      const isNight = !currentPeriod.isDaytime;
      const moonData = this.getMoonPhase(new Date());

      const weatherData: WeatherData = {
        temperature: temperature,
        condition: this.mapWeatherCondition(currentPeriod.shortForecast),
        description: currentPeriod.detailedForecast,
        icon: currentPeriod.icon,
        humidity: currentPeriod.relativeHumidity?.value || 0,
        windSpeed: this.parseWindSpeed(currentPeriod.windSpeed),
        location: this.config.zipCode ? `ZIP ${this.config.zipCode}` : this.config.city,
        isNight,
        moonPhase: moonData.phase,
        moonIllumination: moonData.illumination
      };

      this.cache = weatherData;
      this.lastUpdate = now;

      return weatherData;
    } catch (error) {
      console.error('Failed to fetch weather data:', error);
      // If we have a cache, return it even if expired rather than returning null and causing flashing
      if (this.cache) return this.cache;
      return null;
    }
  }


  private mapWeatherCondition(shortForecast: string): string {
    const forecast = shortForecast.toLowerCase();
    
    if (forecast.includes('sunny') || forecast.includes('clear')) {
      return 'Clear';
    } else if (forecast.includes('cloudy') || forecast.includes('clouds')) {
      return 'Cloudy';
    } else if (forecast.includes('rain') || forecast.includes('showers')) {
      return 'Rain';
    } else if (forecast.includes('snow')) {
      return 'Snow';
    } else if (forecast.includes('thunderstorm') || forecast.includes('storms')) {
      return 'Storm';
    } else if (forecast.includes('fog') || forecast.includes('mist')) {
      return 'Fog';
    } else {
      return 'Cloudy'; // Default fallback
    }
  }

  private parseWindSpeed(windSpeed: string): number {
    // Parse wind speed from strings like "5 to 10 mph" or "15 mph"
    const match = windSpeed.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  }

  getWeatherConditionClass(condition: string): string {
    const conditionMap: Record<string, string> = {
      'clear': 'weather-clear',
      'cloudy': 'weather-cloudy',
      'clouds': 'weather-cloudy',
      'rain': 'weather-rain',
      'drizzle': 'weather-rain',
      'storm': 'weather-storm',
      'thunderstorm': 'weather-storm',
      'snow': 'weather-snow',
      'mist': 'weather-mist',
      'fog': 'weather-fog',
      'haze': 'weather-haze',
      'dust': 'weather-dust',
      'sand': 'weather-dust',
      'ash': 'weather-ash',
      'squall': 'weather-windy',
      'tornado': 'weather-tornado'
    };

    return conditionMap[condition.toLowerCase()] || 'weather-clear';
  }

  getWeatherIcon(condition: string, icon: string): string {
    // Return appropriate icon based on condition and icon code
    const iconMap: Record<string, string> = {
      '01d': '☀️', // clear sky day
      '01n': '🌙', // clear sky night
      '02d': '⛅', // few clouds day
      '02n': '☁️', // few clouds night
      '03d': '☁️', // scattered clouds
      '03n': '☁️',
      '04d': '☁️', // broken clouds
      '04n': '☁️',
      '09d': '🌧️', // shower rain
      '09n': '🌧️',
      '10d': '🌦️', // rain day
      '10n': '🌧️', // rain night
      '11d': '⛈️', // thunderstorm
      '11n': '⛈️',
      '13d': '❄️', // snow
      '13n': '❄️',
      '50d': '🌫️', // mist
      '50n': '🌫️'
    };

    return iconMap[icon] || '🌤️';
  }
}

export const weatherService = new WeatherService();
